import React, { useEffect, useState, useCallback } from "react";
import { getBookingSessionDetail } from "../api/auth";
import { fmtLocation } from "../utils/format";

const fmtDollar = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fullTimestamp(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function relativeTime(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const dt = new Date(dateStr);
  const diffMs = now - dt;
  if (diffMs < 0) return dt.toLocaleDateString();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diffMs / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return dt.toLocaleDateString();
}

function fmtDuration(startStr, endStr) {
  if (!startStr || !endStr) return "—";
  const ms = new Date(endStr) - new Date(startStr);
  if (ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "<1m";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

function fmtDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const OUTCOME_CONFIG = {
  booked:         { label: "Booked",    bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400", dot: "bg-emerald-500" },
  abandoned_cart: { label: "Abandoned", bg: "bg-amber-100 dark:bg-amber-900/40",    text: "text-amber-700 dark:text-amber-400", dot: "bg-amber-500" },
  lead_captured:  { label: "Lead",      bg: "bg-blue-100 dark:bg-blue-900/40",      text: "text-blue-700 dark:text-blue-400", dot: "bg-blue-500" },
  browsing:       { label: "Browsing",  bg: "bg-gray-100 dark:bg-gray-800",          text: "text-gray-600 dark:text-gray-400", dot: "bg-gray-400" },
};

const STEP_LABELS = {
  search: "Search", results: "Results", details: "Details", cart: "Cart",
  checkout: "Checkout", checkout_contact: "Payment", checkout_payment: "Payment", payment: "Payment", confirmation: "Booked",
};
const FUNNEL_STEPS = ["search", "results", "details", "cart", "checkout", "confirmation"];

const RECOVERY_STATUS = {
  pending: { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400" },
  sent: { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-400" },
  delivered: { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-700 dark:text-blue-400" },
  opened: { bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-400" },
  clicked: { bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-700 dark:text-orange-400" },
  converted: { bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400" },
  bounced: { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400" },
  failed: { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-400" },
};

const EVENT_META = {
  page_view:          { label: "Viewed page",        icon: "eye" },
  book_now_click:     { label: "Clicked Book Now",   icon: "click" },
  booking_hover:      { label: "Hovered on booking", icon: "cursor" },
  booking_completed:  { label: "Completed booking",  icon: "check" },
  exit_intent:        { label: "Exit intent",        icon: "exit" },
  email_capture:      { label: "Email captured",     icon: "mail" },
  form_submit:        { label: "Submitted form",     icon: "form" },
  outbound_click:     { label: "Outbound click",     icon: "globe" },
  copy_text:          { label: "Copied text",        icon: "note" },
  document_download:  { label: "Downloaded document", icon: "download" },
  email_click:        { label: "Clicked email link", icon: "mail" },
};

/* Sub-components */
const Row = ({ label, value, highlight, mono }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between items-start gap-3 py-0.5">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <span className={`text-sm text-right ${highlight ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"} ${mono ? "font-mono text-sm" : ""} truncate max-w-[240px]`} title={typeof value === "string" ? value : undefined}>{value}</span>
    </div>
  );
};

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <div className="flex justify-between items-center gap-3 py-0.5">
      <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-[200px]">{value}</span>
        <button type="button" onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 shrink-0">
          {copied
            ? <svg className="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
            : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
          }
        </button>
      </div>
    </div>
  );
};

const Card = ({ title, icon, children, className = "" }) => (
  <div className={`bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 ${className}`}>
    {title && <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">{icon}{title}</h4>}
    <div className="space-y-1">{children}</div>
  </div>
);

/* Icons */
const I = {
  dollar: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>,
  cart: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" /></svg>,
  bed: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 7v11M21 7v11M3 18h18M3 11h18M7 11V7h10v4" /></svg>,
  truck: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="1" y="3" width="15" height="13" /><path d="M16 8h4l3 3v5h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>,
  user: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  clock: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  mail: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
  globe: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>,
  history: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 106 5.3L3 8" /><path d="M12 7v5l4 2" /></svg>,
  star: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
  steps: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
  note: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>,
  map: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>,
  phone: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
  shield: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  cursor: <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /></svg>,
  eye: <svg className="w-4 h-4 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  click: <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" /><path d="M13 13l6 6" /></svg>,
  check: <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>,
  exit: <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" /></svg>,
  download: <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>,
  form: <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>,
};

/* ---- Main ---- */
export default function SessionDetailPanel({ open, onClose, sessionId }) {
  const [session, setSession] = useState(null);
  const [recoveryActions, setRecoveryActions] = useState([]);
  const [visitorHistory, setVisitorHistory] = useState([]);
  const [person, setPerson] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [webSessions, setWebSessions] = useState([]);
  const [trackingEvents, setTrackingEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rightTab, setRightTab] = useState("journey");

  const fetchDetail = useCallback(async (id) => {
    setLoading(true); setError(null);
    try {
      const resp = await getBookingSessionDetail(id);
      const d = resp?.data ?? resp;
      setSession(d?.session || null);
      setRecoveryActions(d?.recovery_actions || []);
      setVisitorHistory(d?.visitor_history || []);
      setPerson(d?.person || null);
      setSuppression(d?.suppression || null);
      setWebSessions(d?.web_sessions || []);
      setTrackingEvents(d?.tracking_events || []);
    } catch (err) { setError(err?.message || "Failed to load session"); setSession(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (sessionId && open) { fetchDetail(sessionId); setRightTab("journey"); } }, [sessionId, open, fetchDetail]);
  useEffect(() => { if (!open) return; const h = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [open, onClose]);

  const s = session;
  const outcomeConf = s ? (OUTCOME_CONFIG[s.outcome] || OUTCOME_CONFIG.browsing) : null;
  const rawStep = s?.deepest_step === "checkout_contact" || s?.deepest_step === "checkout_payment" ? "checkout" : s?.deepest_step;
  const stepIdx = s ? FUNNEL_STEPS.indexOf(rawStep) : -1;
  const isLive = s?.last_activity_at && (Date.now() - new Date(s.last_activity_at).getTime()) < 15 * 60 * 1000;
  const hasRvData = s && (s.rv_type_mentioned || s.rv_length_mentioned || s.equipment_make);
  const totalVisitorSessions = visitorHistory.length + 1;
  const totalVisitorBookings = visitorHistory.filter(h => h.outcome === "booked").length + (s?.outcome === "booked" ? 1 : 0);
  const totalVisitorRevenue = visitorHistory.reduce((sum, h) => sum + (h.value || 0), 0) + (s?.value || 0);

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />

      {/* Wide panel — two-column layout */}
      <div className={`fixed top-0 right-0 h-full w-[920px] max-w-[calc(100vw-60px)] z-50 bg-white dark:bg-gray-900 shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${open ? "translate-x-0" : "translate-x-full"}`}>

        {/* Top bar */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {isLive && (
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">{s?.visitor_name || "Anonymous"}</h2>
                {outcomeConf && <span className={`px-2.5 py-0.5 rounded-full text-sm font-semibold ${outcomeConf.bg} ${outcomeConf.text} shrink-0`}>{outcomeConf.label}</span>}
                {suppression && <span className="px-2 py-0.5 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 shrink-0">Suppressed</span>}
              </div>
              {s && (
                <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  {s.visitor_email && <span>{s.visitor_email}</span>}
                  {s.visitor_phone && <span>· {s.visitor_phone}</span>}
                  {s.guest_city && s.guest_state && <span>· {s.guest_city}, {s.guest_state}</span>}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            {s?.booking_deep_link_url && (
              <a href={s.booking_deep_link_url} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-[#32B1FF] text-white hover:bg-blue-600 transition-colors">
                Open Booking
              </a>
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <svg className="animate-spin h-8 w-8 text-[#32B1FF]" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg p-4">{error}</div>
          </div>
        ) : s ? (
          /* Two-column body */
          <div className="flex-1 flex overflow-hidden">

            {/* LEFT COLUMN — Session details (scrollable) */}
            <div className="w-[420px] shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto p-5 space-y-5">

              {/* KPI row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="flex justify-center mb-1">{I.dollar}</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{s.value > 0 ? fmtDollar(s.value) : "—"}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Cart Value</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="flex justify-center mb-1">{I.steps}</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{STEP_LABELS[s.deepest_step] || s.deepest_step || "—"}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Deepest Step</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-center">
                  <div className="flex justify-center mb-1">{I.clock}</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{fmtDuration(s.first_seen_at, s.last_activity_at)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Duration</div>
                </div>
              </div>

              {/* Funnel */}
              {stepIdx >= 0 && (
                <div className="flex gap-1">
                  {FUNNEL_STEPS.map((step, i) => (
                    <div key={step} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`w-full h-2 rounded-full ${i <= stepIdx ? (i === FUNNEL_STEPS.length - 1 && s.reservation_made ? "bg-emerald-500" : "bg-[#32B1FF]") : "bg-gray-200 dark:bg-gray-700"}`} />
                      <span className={`text-sm ${i <= stepIdx ? "text-gray-700 dark:text-gray-300 font-medium" : "text-gray-400 dark:text-gray-500"}`}>{STEP_LABELS[step]}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Badges */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{s.device || "Unknown"}</span>
                {s.traffic_source && <span className="px-2 py-0.5 rounded-full text-sm bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-400">{s.traffic_source}</span>}
                {person?.lifecycle_stage && <span className="px-2 py-0.5 rounded-full text-sm bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400">{person.lifecycle_stage.replace(/_/g, " ")}</span>}
                {person?.engagement_score > 0 && <span className="px-2 py-0.5 rounded-full text-sm bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400">Score: {person.engagement_score}</span>}
              </div>

              {/* Person profile */}
              {person && (
                <Card title="Visitor Profile" icon={I.star}>
                  <div className="grid grid-cols-3 gap-2 text-center mb-2">
                    <div><div className="text-base font-bold text-gray-900 dark:text-gray-100">{person.total_sessions ?? 0}</div><div className="text-sm text-gray-500">Sessions</div></div>
                    <div><div className="text-base font-bold text-gray-900 dark:text-gray-100">{person.booking_count ?? 0}</div><div className="text-sm text-gray-500">Bookings</div></div>
                    <div><div className="text-base font-bold text-gray-900 dark:text-gray-100">{person.total_booking_value ? fmtDollar(person.total_booking_value) : "—"}</div><div className="text-sm text-gray-500">LTV</div></div>
                  </div>
                  <Row label="Preferred Device" value={person.preferred_device} />
                  <Row label="Top Source" value={person.top_traffic_source} />
                  {person.first_touch_source && <Row label="First Touch" value={[person.first_touch_source, person.first_touch_medium].filter(Boolean).join(" / ")} />}
                  {person.first_touch_landing_page && <Row label="Landing Page" value={person.first_touch_landing_page.replace(/https?:\/\/[^/]+/, "")} />}
                </Card>
              )}

              {/* Booking Details */}
              <Card title="Cart Details" icon={I.cart}>
                {s.reservation_made && <Row label="Reservation" value="Confirmed" highlight />}
                <Row label="Cart Value" value={s.value > 0 ? fmtDollar(s.value) : null} highlight />
                <Row label="Nightly Rate" value={s.nightly_rate > 0 ? fmtDollar(s.nightly_rate) : null} />
                <Row label="Site Type" value={s.specific_site_type} />
                <Row label="Site Category" value={s.site_category} />
                <Row label="Site Number" value={s.site_number} />
                {Array.isArray(s.accommodation_type_asked) && s.accommodation_type_asked.length > 0 && <Row label="Types Browsed" value={s.accommodation_type_asked.join(", ")} />}
                <Row label="Booking Window" value={s.booking_window_days ? `${s.booking_window_days} days ahead` : null} />
                <Row label="Transaction ID" value={s.transaction_id} mono />
                {/* Ecommerce line items */}
                {s.ecommerce_items && (() => {
                  try {
                    const items = typeof s.ecommerce_items === "string" ? JSON.parse(s.ecommerce_items) : s.ecommerce_items;
                    if (Array.isArray(items) && items.length > 0) return (
                      <div className="pt-2 mt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1.5">Cart Items</div>
                        {items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between py-1">
                            <div className="min-w-0">
                              <span className="text-sm text-gray-700 dark:text-gray-300">{item.item_name?.replace(/_/g, " ") || "Item"}</span>
                              {item.item_category2 && <span className="text-sm text-gray-400 ml-1">({item.item_category2})</span>}
                            </div>
                            {item.price > 0 && <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">{fmtDollar(item.price)}</span>}
                          </div>
                        ))}
                      </div>
                    );
                  } catch (_) {}
                  return null;
                })()}
              </Card>

              {/* Stay Details */}
              {(s.check_in_date_mentioned || s.num_nights_mentioned) && (
                <Card title="Stay Details" icon={I.bed}>
                  <Row label="Check-in" value={fmtDate(s.check_in_date_mentioned)} />
                  <Row label="Check-out" value={fmtDate(s.check_out_date_mentioned)} />
                  <Row label="Nights" value={s.num_nights_mentioned > 0 ? s.num_nights_mentioned : null} />
                  <Row label="Adults" value={s.num_adults_mentioned > 0 ? s.num_adults_mentioned : null} />
                  <Row label="Children" value={s.num_children_mentioned > 0 ? s.num_children_mentioned : null} />
                  <Row label="Pets" value={s.num_pets_mentioned > 0 ? s.num_pets_mentioned : null} />
                </Card>
              )}

              {/* RV */}
              {hasRvData && (
                <Card title="RV Details" icon={I.truck}>
                  <Row label="Type" value={s.rv_type_mentioned} />
                  <Row label="Length" value={s.rv_length_mentioned ? `${s.rv_length_mentioned} ft` : null} />
                  <Row label="Make" value={s.equipment_make} />
                  <Row label="Model" value={s.equipment_model} />
                  <Row label="Slide-Outs" value={s.slide_outs_mentioned} />
                </Card>
              )}

              {/* Contact */}
              <Card title="Contact" icon={I.user}>
                <CopyRow label="Name" value={s.visitor_name} />
                <CopyRow label="Email" value={s.visitor_email} />
                <CopyRow label="Phone" value={s.visitor_phone} />
                {s.visitor_phone_2 && <CopyRow label="Phone 2" value={s.visitor_phone_2} />}
                <Row label="Address" value={[s.guest_address, s.guest_city, s.guest_state, s.guest_postal_code].filter(Boolean).join(", ") || null} />
                <Row label="Country" value={s.guest_country} />
                {(s.email_opt_in || s.sms_opt_in) && (
                  <div className="flex gap-3 pt-1">
                    {s.email_opt_in && <span className="text-sm text-emerald-600 dark:text-emerald-400">Email opt-in</span>}
                    {s.sms_opt_in && <span className="text-sm text-emerald-600 dark:text-emerald-400">SMS opt-in</span>}
                  </div>
                )}
              </Card>

              {/* Preferences */}
              {(s.reason_for_visit || s.special_requests) && (
                <Card title="Guest Notes" icon={I.note}>
                  <Row label="Reason" value={s.reason_for_visit} />
                  <Row label="Special Requests" value={s.special_requests} />
                </Card>
              )}

              {/* Timeline */}
              <Card title="Session Timeline" icon={I.clock}>
                <Row label="First Seen" value={fullTimestamp(s.first_seen_at)} />
                <Row label="Last Activity" value={fullTimestamp(s.last_activity_at)} />
                <Row label="Duration" value={fmtDuration(s.first_seen_at, s.last_activity_at)} />
                <Row label="Referral" value={s.referral_source} />
                <Row label="Traffic" value={s.traffic_source} />
                {s.page_url && <Row label="Page" value={s.page_url.replace(/https?:\/\/[^/]+/, "")} />}
              </Card>
            </div>

            {/* RIGHT COLUMN — Contextual data with tabs */}
            <div className="flex-1 flex flex-col overflow-hidden">

              {/* Tab bar */}
              <div className="px-5 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700 flex gap-1 shrink-0">
                {[
                  { key: "journey", label: "Customer Journey", count: webSessions.length || trackingEvents.length ? (webSessions.length + trackingEvents.length) : 0, icon: I.steps },
                  { key: "recovery", label: "Recovery", count: recoveryActions.length, icon: I.mail },
                  { key: "history", label: "Visit History", count: totalVisitorSessions, icon: I.history },
                ].map(({ key, label, count, icon }) => (
                  <button key={key} type="button" onClick={() => setRightTab(key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      rightTab === key ? "bg-[#32B1FF] text-white [&_svg]:text-white" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}>
                    <span className="scale-[0.85]">{icon}</span>
                    {label}{count > 0 ? ` (${count})` : ""}
                  </button>
                ))}
              </div>

              {/* Tab content (scrollable) */}
              <div className="flex-1 overflow-y-auto p-5">

                {/* Journey */}
                {rightTab === "journey" && (
                  <div className="space-y-5">
                    {trackingEvents.length > 0 && (
                      <div>
                        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{I.clock} Activity Timeline</h4>
                        <div className="relative pl-8 border-l-2 border-gray-200 dark:border-gray-700 space-y-4">
                          {trackingEvents.map((e, i) => {
                            const meta = EVENT_META[e.event_type] || { label: e.event_type?.replace(/_/g, " "), icon: "eye" };
                            const isBooking = e.event_type === "booking_completed" || e.event_type === "book_now_click";
                            const eventIcon = I[meta.icon] || I.eye;
                            return (
                              <div key={i} className="relative">
                                <div className="absolute -left-[29px] w-5 h-5 rounded-full bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center">
                                  <span className="scale-[0.7]">{eventIcon}</span>
                                </div>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <span className={`text-sm font-medium ${isBooking ? "text-emerald-600 dark:text-emerald-400" : "text-gray-700 dark:text-gray-300"}`}>{meta.label}</span>
                                    {e.page_title && <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{e.page_title}</div>}
                                    {e.event_data && (typeof e.event_data === "object"
                                      ? (e.event_data.link_text || e.event_data.button_text) && <div className="text-sm text-[#32B1FF] truncate">"{e.event_data.link_text || e.event_data.button_text}"</div>
                                      : null
                                    )}
                                    {e.geo_city && <div className="flex items-center gap-1 text-sm text-gray-400 dark:text-gray-500"><span className="scale-[0.8]">{I.map}</span>{fmtLocation(e.geo_city, e.geo_region, e.geo_country)}</div>}
                                  </div>
                                  <span className="text-sm text-gray-400 shrink-0">{relativeTime(e.event_timestamp)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {webSessions.length > 0 && (
                      <div>
                        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{I.globe} Website Visits</h4>
                        <div className="space-y-2">
                          {webSessions.map((ws) => (
                            <div key={ws.session_id} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{relativeTime(ws.session_start)}</span>
                                  {ws.had_booking_event && <span className="px-1.5 py-0.5 text-sm rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400">Booking</span>}
                                  {ws.is_bounce && <span className="px-1.5 py-0.5 text-sm rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">Bounce</span>}
                                </div>
                                <span className="text-sm text-gray-500">{ws.pages_viewed} pages · {ws.duration_seconds ? `${Math.round(ws.duration_seconds / 60)}m` : "—"}</span>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                <span>{ws.traffic_channel_group || ws.traffic_source || "Direct"}</span>
                                <span>{ws.device_type}</span>
                                {ws.geo_city && <span>{fmtLocation(ws.geo_city, ws.geo_region, ws.geo_country)}</span>}
                              </div>
                              {ws.landing_page && <div className="text-sm text-gray-400 truncate mt-0.5">{ws.landing_page.replace(/https?:\/\/[^/]+/, "")}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!trackingEvents.length && !webSessions.length && (
                      <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No journey data available for this visitor.</div>
                    )}
                  </div>
                )}

                {/* Recovery */}
                {rightTab === "recovery" && (
                  <div className="space-y-3">
                    {recoveryActions.length === 0 ? (
                      <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No recovery emails sent for this session.</div>
                    ) : recoveryActions.map((a) => {
                      const sc = RECOVERY_STATUS[a.status] || RECOVERY_STATUS.pending;
                      return (
                        <div key={a.id} className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">Email #{a.campaign_sequence || 1}</span>
                              <span className={`px-2 py-0.5 rounded-full text-sm font-semibold ${sc.bg} ${sc.text}`}>{a.status}</span>
                            </div>
                            {a.converted_value > 0 && <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtDollar(a.converted_value)}</span>}
                          </div>
                          {a.subject && <div className="text-sm text-gray-600 dark:text-gray-400 italic">"{a.subject}"</div>}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                            <Row label="Sent" value={a.sent_at ? relativeTime(a.sent_at) : "—"} />
                            <Row label="By" value={a.created_by || "auto"} />
                            <Row label="Delivered" value={a.delivered_at ? relativeTime(a.delivered_at) : "—"} />
                            <Row label="Opened" value={a.opened_at ? relativeTime(a.opened_at) : "—"} />
                            <Row label="Clicked" value={a.clicked_at ? relativeTime(a.clicked_at) : "—"} />
                            {a.converted_at && <Row label="Converted" value={relativeTime(a.converted_at)} />}
                          </div>
                          {a.body_preview && (
                            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                              {a.body_preview}...
                            </div>
                          )}
                          {a.ai_model_used && (
                            <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-400 dark:text-gray-500">
                              <span className="scale-[0.85]">{I.shield}</span>
                              <span>AI: {a.ai_model_used}</span>
                              {a.ai_prompt_summary && <span>· {a.ai_prompt_summary}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* History */}
                {rightTab === "history" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalVisitorSessions}</div>
                        <div className="text-sm text-gray-500">Sessions</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{totalVisitorBookings}</div>
                        <div className="text-sm text-gray-500">Bookings</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                        <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{fmtDollar(totalVisitorRevenue)}</div>
                        <div className="text-sm text-gray-500">Lifetime Value</div>
                      </div>
                    </div>

                    {visitorHistory.length === 0 ? (
                      <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">This is the only session from this visitor.</div>
                    ) : (
                      <div className="space-y-2">
                        {visitorHistory.map((h) => {
                          const ho = OUTCOME_CONFIG[h.outcome] || OUTCOME_CONFIG.browsing;
                          return (
                            <div key={h.id} className="flex items-center justify-between py-2.5 px-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${ho.dot}`} />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{h.specific_site_type || STEP_LABELS[h.deepest_step] || "Browsing"}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-sm ${ho.bg} ${ho.text}`}>{ho.label}</span>
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 ml-4">
                                  {relativeTime(h.first_seen_at)}{h.check_in_date_mentioned && ` · Check-in ${fmtDate(h.check_in_date_mentioned)}`}
                                </div>
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{h.value > 0 ? fmtDollar(h.value) : "—"}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
